import { cn } from '@/lib/utils'
import { Plus } from 'lucide-react'
import { Button, EmptyState } from '@teach/ui'
import { CourseCardOverlay } from '@/components/catalog/CourseCardOverlay'
import { CourseCardSkeletonGrid } from '@/components/catalog/CourseCardSkeletonGrid'
import { courseCardStatus } from '@/components/catalog/CourseStatusChip'
import { CreateCourseCard } from './CreateCourseCard'
import { CARD_GRID_CLASS } from '@/components/catalog/cardGrid'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { matchesDashboardFilter, matchesDashboardSearch } from './dashboardFilter'
import type { DashboardFilter } from './DashboardHeader'
import type { Course } from '@/types/course'
import {
  publishedTranslationLangs,
  unpublishedFilledLang,
} from '@/features/editor/lib/courseTranslation'
import { isStartedTranslationRun, selectJobsByCourse } from '@/features/ai/jobsSlice'
import { useAppSelector } from '@/store/hooks'
import { useRevealOnScroll } from '@/lib/useRevealOnScroll'

const GRID = CARD_GRID_CLASS

interface Props {
  courses: Course[]
  isLoading: boolean
  filter: DashboardFilter
  search: string
  onRequestDelete: (course: Course) => void
  onCreate: () => void
  isCreating: boolean
}

export const DashboardCourseGrid = ({
  courses,
  isLoading,
  filter,
  search,
  onRequestDelete,
  onCreate,
  isCreating,
}: Props) => {
  // Реестр джоб целиком, а не хук на курс: хуки в цикле по карточкам звать
  // нельзя. Нужен затем же, зачем вкладке «Языки»: строку перевода бэк заводит
  // вместе с джобой, а список курсов до терминального статуса не перечитывается
  // — без этого приглушённый бейдж висел бы весь прогон перевода.
  const jobs = useAppSelector(selectJobsByCourse)
  const m = useMessages(messages)

  const query = search.trim()
  const filtered = courses.filter(
    (c) => matchesDashboardFilter(c, filter) && matchesDashboardSearch(c, search),
  )

  // Все курсы уже на руках (фильтры и счётчики — по всем), рисуем порциями при прокрутке.
  const { shown, hasMore, sentinelRef } = useRevealOnScroll(filtered.length, `${filter}|${query}`)

  if (isLoading) {
    return <CourseCardSkeletonGrid count={8} className={GRID} />
  }

  if (filtered.length === 0) {
    return (
      // Маскот по центру свободной области страницы, а не прижатым к шапке:
      // под фильтрами остаётся весь экран, и группа на самом верху выглядела брошенной.
      <div
        className={cn(
          'flex items-center justify-center',
          courses.length === 0 ? 'min-h-[calc(100dvh-10rem)]' : 'min-h-[calc(100dvh-22rem)]',
        )}
      >
        <EmptyState
          className="py-0"
          title={courses.length === 0 ? m.noCoursesYet : m.nothingFound}
          action={
            courses.length === 0 ? (
              // На телефоне — во всю ширину пузыря: в него проще попасть пальцем.
              <Button onClick={onCreate} disabled={isCreating} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                {m.createCourse}
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return (
    <>
      <div className={GRID}>
        {query ? null : <CreateCourseCard onCreate={onCreate} isCreating={isCreating} />}
        {filtered.slice(0, shown).map((c) => (
          <CourseCardOverlay
            key={c.id}
            id={c.id}
            name={c.name}
            nameEn={c.nameEn}
            banner={c.banner}
            rating={c.rating?.value}
            updatedAt={c.updatedAt}
            completedCount={c.amountOfPeopleCompleted ?? 0}
            variant="workshop"
            status={courseCardStatus(c)}
            onDelete={() => onRequestDelete(c)}
            sourceLang={c.sourceLang}
            publishedLangs={publishedTranslationLangs(c)}
            unpublishedFilledLang={
              unpublishedFilledLang(c, isStartedTranslationRun(jobs[c.id])) ?? undefined
            }
          />
        ))}
      </div>
      {hasMore ? <div ref={sentinelRef} aria-hidden className="h-px" /> : null}
    </>
  )
}
